/**
 * IT TASK DETAIL PAGE
 *
 * Instance mapping form for IT setup tasks.
 * Allows IT team to map instances to locations and create BigQuery resources.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/status-badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { MapPin, Database, ArrowLeft, Clock } from 'lucide-react';
import { InstanceMappingForm } from '@/components/instance-mapping-form';
import { ClientMeetingStatusCard } from '@/components/client-meeting-status-card';
import type { Task, Organization, Location } from '@/types/database';

interface TaskWithRelations extends Task {
  organization: Pick<Organization, 'id' | 'name' | 'status' | 'logo_url'> | null;
  location: Location | null;
}

function ITTaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [task, setTask] = useState<TaskWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [contactInfo, setContactInfo] = useState<any>(null);

  // Auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'IT_TEAM' && user.role !== 'IT_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    navigate('/unauthorized');
    return null;
  }

  // Fetch task data
  const fetchTask = async (silent = false) => {
    if (!taskId) {
      navigate('/it-tasks');
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      const supabase = createClient();

      // Fetch task with organization and location details
      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .select(`
          *,
          organization:organizations(id, name, status, logo_url),
          location:locations(
            id,
            name,
            description,
            logo_url,
            source_schema,
            open_dental_source_schema,
            open_dental_clinic,
            source_name,
            status,
            mapping_status,
            client_meeting_status
          )
        `)
        .eq('id', taskId)
        .eq('type', 'IT_PROVISIONING')
        .single();

      if (taskError) {
        console.error('Error fetching task:', taskError);
        navigate('/it-tasks');
        return;
      }

      if (!taskData) {
        navigate('/it-tasks');
        return;
      }

      setTask(taskData as TaskWithRelations);

      // Fetch onboarding_queue data separately using organization ID
      if (taskData.organization_id) {
        const { data: queueData } = await supabase
          .from('onboarding_queue')
          .select('primary_contact_name, primary_contact_email, phone_number')
          .eq('id', taskData.organization_id)
          .maybeSingle();

        if (queueData) {
          setContactInfo(queueData);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      console.error('Fetch error:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Data fetching on mount
  useEffect(() => {
    fetchTask();
  }, [taskId, navigate]);

  // Handle client meeting status change
  const handleMeetingStatusChange = (newStatus: 'PENDING' | 'COMPLETED') => {
    if (newStatus === 'COMPLETED') {
      // If meeting is completed, navigate back to IT tasks list
      // The list will now show all individual location tasks
      navigate('/it-tasks');
    } else {
      // Refetch task data silently to update location with new client_meeting_status
      fetchTask(true);
    }
  };

  // Render states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading task</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  const isMeetingCompleted = (task.location as any)?.client_meeting_status === 'COMPLETED';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <button
        onClick={() => navigate('/it-tasks')}
        className="flex items-center gap-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to IT Tasks</span>
      </button>

      {/* Header - Focus on Location */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          {/* Location Logo */}
          {task.location?.logo_url ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
                  <img
                    src={task.location.logo_url}
                    alt={`${task.location?.name} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <MapPin className="w-10 h-10 text-gray-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          )}

          {/* Header Content */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.location?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Location</TooltipContent>
              </Tooltip>
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.organization?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Organization</TooltipContent>
              </Tooltip>
            </p>
            <div className="mt-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusBadge status={task.status} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Status</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Client Meeting Status - Primary Action (at top for visibility) */}
      <ClientMeetingStatusCard
        location={task.location}
        task={task}
        userId={user.id}
        onStatusChange={handleMeetingStatusChange}
      />

      {/* Primary Contact Information */}
      {contactInfo && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Primary Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {contactInfo.primary_contact_name && (
                <div>
                  <p className="text-xs text-muted-foreground">Contact Name</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_name}</p>
                </div>
              )}
              {contactInfo.primary_contact_email && (
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_email}</p>
                </div>
              )}
              {contactInfo.phone_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium text-sm">{contactInfo.phone_number}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location Details - Information Section */}
      {isMeetingCompleted ? <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Description */}
          {task.location?.description && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{task.location.description}</p>
            </div>
          )}

          {/* Status fields (read-only info) */}
          {(task.location?.status || task.location?.mapping_status) && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              {task.location?.status && (
                <div>
                  <p className="text-xs text-muted-foreground">Location Status</p>
                  <p className="font-medium text-sm">{task.location.status}</p>
                </div>
              )}
              {task.location?.mapping_status && (
                <div>
                  <p className="text-xs text-muted-foreground">Mapping Status</p>
                  <p className="font-medium text-sm">{task.location.mapping_status}</p>
                </div>
              )}
            </div>
          )}

          {/* Non-'na' fields - Display as readonly info */}
          {((task.location as any)?.source_schema && (task.location as any).source_schema !== 'na') ||
          ((task.location as any)?.source_name && (task.location as any).source_name !== 'na') ? (
            <div className="pt-4 border-t space-y-3">
            {(task.location as any)?.source_schema && (task.location as any).source_schema !== 'na' && (
              <div>
                <p className="text-xs text-muted-foreground">Source Schema</p>
                <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded">
                  {(task.location as any).source_schema}
                </p>
              </div>
            )}

              {(task.location as any)?.source_name && (task.location as any).source_name !== 'na' && (
                <div>
                  <p className="text-xs text-muted-foreground">Source Name</p>
                  <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded">
                    {(task.location as any).source_name}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card> : null}

      {/* Current Configuration (if exists) */}
      {isMeetingCompleted ? (
        <>
          {(task as any)?.metadata?.instance_name && (
            <Card className="mb-6 border-green-200 dark:border-green-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Current Configuration
                </CardTitle>
                <CardDescription>
                  This location has already been configured
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Instance Name</p>
                    <p className="font-medium font-mono text-sm">
                      {(task as any).metadata.instance_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">BigQuery Dataset</p>
                    <p className="font-medium font-mono text-sm">
                      {(task as any).metadata.bigquery_dataset || 'Not set'}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">BigQuery Table</p>
                  <p className="font-medium font-mono text-sm">
                    {(task as any).metadata.bigquery_table || 'Not set'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Instance Mapping Form */}
          <Card>
            <CardHeader>
              <CardTitle>
                {task.status === 'COMPLETED' ? 'Configuration Complete' : 'OpenDental Configuration'}
              </CardTitle>
              <CardDescription>
                {task.status === 'COMPLETED'
                  ? 'This task has been completed. You can update the configuration if needed.'
                  : 'Configure OpenDental settings for this location'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InstanceMappingForm
                task={task}
                location={task.location}
                userId={user.id}
              />
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center p-8 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Awaiting Client Meeting
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Please complete the client meeting and mark it as done above to proceed with the IT configuration tasks for this location.
          </p>
        </div>
      )}
    </div>
  );
}

export default ITTaskDetailPage;
